import axios from 'axios';
import { authService } from './authService';
import { API_CONFIG } from '../config/apiConfig';

const apiClient = axios.create({
    baseURL: API_CONFIG.baseUrl,
});

// Request interceptor to add the bearer token
apiClient.interceptors.request.use(
    async (config) => {
        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);

        // Always try to get token, even if isAuthenticated() returns false
        // This handles edge cases during navigation
        let token = null;

        if (authService.isAuthenticated()) {
            console.log('[API Request] User is authenticated, attempting to get token...');

            // Try to refresh token if it's about to expire (min 5 seconds)
            try {
                const refreshed = await authService.updateToken(5);
                if (refreshed) {
                    console.log('[API Request] Token was refreshed');
                }
            } catch (error) {
                console.error('[API Request] Token refresh failed:', error);
            }

            token = authService.getToken();
        } else {
            console.warn('[API Request] isAuthenticated() returned false, checking localStorage directly...');

            // Fallback: Try to get token directly from localStorage
            token = localStorage.getItem('kc_token');

            if (token) {
                console.log('[API Request] ✓ Token found in localStorage (length:', token.length, ')');
                console.log('[API Request] Restoring authentication state...');
                // This will trigger the restoration logic in isAuthenticated()
                authService.isAuthenticated();
            } else {
                console.error('[API Request] ✗ No token in localStorage either');
            }
        }

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('[API Request] ✓ Authorization header added');
        } else {
            console.error('[API Request] ✗ No token available - request will fail');
        }

        return config;
    },
    (error) => {
        console.error('[API Request] Interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor to handle unauthorized errors and subscription warnings/expiration
apiClient.interceptors.response.use(
    (response) => {
        // Check for subscription warning header
        if (response.headers && response.headers['x-subscription-warning'] === 'true') {
            const daysLeft = response.headers['x-subscription-days-left'];
            window.dispatchEvent(new CustomEvent('subscription-warning', { detail: { daysLeft: parseInt(daysLeft, 10) || 0 } }));
        }
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            console.error('[API Response] 401 Unauthorized received — session expired or token invalid. Redirecting to login.');
            authService.logout();
        } else if (error.response?.status === 403 && (error.response?.data?.code === 'SUBSCRIPTION_EXPIRED' || error.response?.data?.code === 'TENANT_INACTIVE')) {
            console.warn('[API Response] Subscription expired or tenant inactive:', error.response.data);
            window.dispatchEvent(new CustomEvent('subscription-expired', { detail: error.response.data }));
        }
        return Promise.reject(error);
    }
);

export default apiClient;
